import axios from 'axios';
import { AuthService } from './auth.service';
import { ENDPOINTS } from '../constants/endpoints';
import {
    ISalaryComponent,
    IEmployeeSalary,
    IEmployeeSalaryCreate,
    IPayrollSettings,
    IPayroll,
    IPayrollPageResponse,
} from '../interfaces/payroll.interface';
import {
    IPage,
    IPayrollPaymentStatus,
    IRecordBatchRequest,
    IRecordPaymentRequest,
    IReversePaymentRequest,
    ISalaryPayment,
    ISalaryPaymentBatch,
    IVoidBatchResponse,
    JournalPostStatus,
} from '../interfaces/salaryPayment';

export const PayrollService = {
    // Salary Components
    getSalaryComponents: async (institutionId?: string): Promise<ISalaryComponent[]> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.get(ENDPOINTS.SALARY_COMPONENT.BY_INSTITUTION(instId), {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    createSalaryComponent: async (data: Partial<ISalaryComponent>, institutionId?: string): Promise<ISalaryComponent> => {
        data.institutionId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.post(ENDPOINTS.SALARY_COMPONENT.BASE, data, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    updateSalaryComponent: async (id: string, data: Partial<ISalaryComponent>): Promise<ISalaryComponent> => {
        const response = await axios.put(ENDPOINTS.SALARY_COMPONENT.UPDATE(id), data, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    /** Sets the ledger account a component posts to; null returns it to the by-name default. */
    setSalaryComponentAccount: async (id: string, accountId: string | null): Promise<ISalaryComponent> => {
        const response = await axios.put(ENDPOINTS.SALARY_COMPONENT.SET_ACCOUNT(id), { accountId }, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    deleteSalaryComponent: async (id: string): Promise<void> => {
        await axios.delete(ENDPOINTS.SALARY_COMPONENT.DELETE(id), {
            headers: AuthService.getAuthHeader()
        });
    },

    seedDefaultSalaryComponents: async (institutionId: string): Promise<void> => {
        await axios.post(ENDPOINTS.SALARY_COMPONENT.SEED_DEFAULTS(institutionId), {}, {
            headers: AuthService.getAuthHeader()
        });
    },

    // Employee Salaries
    getEmployeeSalaries: async (institutionId?: string): Promise<IEmployeeSalary[]> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.get(ENDPOINTS.EMPLOYEE_SALARY.BY_INSTITUTION(instId), {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    getEmployeeSalaryForUser: async (userId: string, institutionId?: string): Promise<IEmployeeSalary> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.get(ENDPOINTS.EMPLOYEE_SALARY.BY_USER(instId, userId), {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    createEmployeeSalary: async (data: IEmployeeSalaryCreate): Promise<IEmployeeSalary> => {
        const response = await axios.post(ENDPOINTS.EMPLOYEE_SALARY.BASE, data, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    updateEmployeeSalary: async (id: string, data: IEmployeeSalaryCreate): Promise<IEmployeeSalary> => {
        const response = await axios.put(ENDPOINTS.EMPLOYEE_SALARY.UPDATE(id), data, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    // Payroll Settings
    getPayrollSettings: async (institutionId?: string): Promise<IPayrollSettings | null> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.get(ENDPOINTS.PAYROLL_SETTINGS.BY_INSTITUTION(instId), {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    updatePayrollSettings: async (data: Partial<IPayrollSettings>, institutionId?: string): Promise<IPayrollSettings> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.put(ENDPOINTS.PAYROLL_SETTINGS.BY_INSTITUTION(instId), data, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    // Payroll
    generatePayroll: async (year: number, month: number, institutionId?: string): Promise<IPayroll[]> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.post(ENDPOINTS.PAYROLL.GENERATE, { institutionId: instId, year, month }, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    getPayrollList: async (year: number, month: number, page: number = 0, size: number = 50, institutionId?: string): Promise<IPayrollPageResponse> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.get(ENDPOINTS.PAYROLL.LIST(instId, year, month), {
            params: { page, size },
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    getPayrollById: async (id: string): Promise<IPayroll> => {
        const response = await axios.get(ENDPOINTS.PAYROLL.BY_ID(id), {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    finalizePayroll: async (id: string): Promise<IPayroll> => {
        const response = await axios.patch(ENDPOINTS.PAYROLL.FINALIZE(id), {}, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    finalizeAllPayroll: async (year: number, month: number, institutionId?: string): Promise<void> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        await axios.patch(ENDPOINTS.PAYROLL.FINALIZE_ALL(instId, year, month), {}, {
            headers: AuthService.getAuthHeader()
        });
    },

    downloadPayslip: async (id: string): Promise<void> => {
        const response = await axios.get(ENDPOINTS.PAYROLL.PAYSLIP(id), {
            headers: AuthService.getAuthHeader(),
            responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `payslip-${id}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    },

    // Salary Payments
    recordPayment: async (payrollId: string, req: IRecordPaymentRequest): Promise<ISalaryPayment> => {
        const response = await axios.post(ENDPOINTS.PAYROLL.RECORD_PAYMENT(payrollId), req, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    reversePayment: async (paymentId: string, req: IReversePaymentRequest): Promise<ISalaryPayment> => {
        const response = await axios.post(ENDPOINTS.PAYROLL.REVERSE_PAYMENT(paymentId), req, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    getPayrollPaymentStatus: async (payrollId: string): Promise<IPayrollPaymentStatus> => {
        const response = await axios.get(ENDPOINTS.PAYROLL.PAYMENT_STATUS(payrollId), {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    retryJournalPost: async (paymentId: string): Promise<ISalaryPayment> => {
        const response = await axios.post(ENDPOINTS.PAYROLL.RETRY_JOURNAL_POST(paymentId), {}, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    skipJournalPost: async (paymentId: string, reason: string): Promise<ISalaryPayment> => {
        const response = await axios.post(
            ENDPOINTS.PAYROLL.SKIP_JOURNAL_POST(paymentId),
            { reason },
            { headers: AuthService.getAuthHeader() }
        );
        return response.data;
    },

    // Batch payments
    recordBatch: async (req: IRecordBatchRequest): Promise<ISalaryPaymentBatch> => {
        const response = await axios.post(ENDPOINTS.PAYROLL.RECORD_BATCH, req, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    voidBatch: async (batchId: string, reason: string): Promise<IVoidBatchResponse> => {
        const response = await axios.post(
            ENDPOINTS.PAYROLL.VOID_BATCH(batchId),
            { reason },
            { headers: AuthService.getAuthHeader() }
        );
        return response.data;
    },

    listBatches: async (
        institutionId?: string,
        page: number = 0,
        size: number = 50
    ): Promise<IPage<ISalaryPaymentBatch>> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.get(ENDPOINTS.PAYROLL.LIST_BATCHES, {
            params: { institutionId: instId, page, size },
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    getPaymentReconciliation: async (
        institutionId?: string,
        status: JournalPostStatus = 'FAILED',
        page: number = 0,
        size: number = 50
    ): Promise<IPage<ISalaryPayment>> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.get(ENDPOINTS.PAYROLL.RECONCILIATION, {
            params: { institutionId: instId, status, page, size },
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    // Segment assignment
    updatePayrollSegment: async (id: string, segmentId?: string): Promise<IPayroll> => {
        const response = await axios.put(ENDPOINTS.PAYROLL.SEGMENT(id), { segmentId: segmentId || undefined }, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    bulkUpdatePayrollSegment: async (payrollIds: string[], segmentId?: string): Promise<void> => {
        await axios.put(ENDPOINTS.PAYROLL.BULK_SEGMENT, { payrollIds, segmentId: segmentId || undefined }, {
            headers: AuthService.getAuthHeader()
        });
    },
};
